import React from 'react';
import styled from '@emotion/styled';
import { Paper, Popper, PopperProps } from '@mui/material';
import { Autocomplete, Grid, Link, TextField } from '@qbe/ads-core';
import { FormDataType } from 'contexts/FormProvider';
import { AddressDataType, useSearchAddress } from 'hooks/useSearchAddress';
import { useStructuredAddress } from 'hooks/useStructuredAddress';
import { debounce, get, isEmpty } from 'lodash';
import { UseFormReturn } from 'react-hook-form';
import { AUSTRALIA, MIN_ADDRESS_SEARCH_STRING } from 'utils/constants';

import { useContent, useContentContext } from '@qbe/dfc-content';
import { createFormErrorMessage } from 'utils/helperMethods';

type AddressType = 'residential' | 'physical' | 'postal' | 'repPostal';

interface ManualAddressFields {
  country: keyof FormDataType;
  streetAddress: keyof FormDataType;
  suburb: keyof FormDataType;
  state: keyof FormDataType;
  postCode: keyof FormDataType;
}
interface AddressAutocomplete {
  form: UseFormReturn<FormDataType>;
  fieldName: keyof FormDataType;
  fieldNameIsValid: keyof FormDataType;
  fieldNameIsManual: keyof FormDataType;
  linkType: AddressType;
  label: string;
  addressFieldNames: ManualAddressFields; // optional until all address field are done
  dataTrack?: string;
  dataTrackValue?: 'mask';
  isOptional?: boolean;
}

export const ManualText = styled.label`
  flex-grow: 1;
  margin-top: 24px;
  color: #0a1f44;
  height: 79px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1px;
  line-height: 18px;
  white-space: pre-wrap;
`;

export const ManualTextContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 0 16px;
  background-color: #f1f2f4;
`;

export const AutoCompleteManualText = styled.p`
  color: #0a1f44;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  letter-spacing: 0;
  line-height: 21px;
`;

const AddressAutocomplete = ({
  form,
  fieldName,
  fieldNameIsValid,
  fieldNameIsManual,
  label,
  linkType,
  addressFieldNames,
  dataTrack = fieldName || label,
  dataTrackValue,
  isOptional,
}: AddressAutocomplete) => {
  const {
    watch,
    formState: { errors },
    setValue,
    trigger: triggerValidation,
    setError,
    clearErrors: clearError,
  } = form;
  const {
    searchAddress,
    addressList,
    error: addressError,
    clearAddressList,
    searchValue,
    searching,
  } = useSearchAddress();

  const { getAddressData } = useStructuredAddress();

  const { getTextContent } = useContent({ context: useContentContext() });
  // Localized content constants
  const addressUnavailableText = getTextContent('addressAutocomplete.unavailable');
  const includeUnitNumberText = getTextContent('addressAutocomplete.includeUnitNumber');
  const cantFindAddressText = getTextContent('addressAutocomplete.cantFindTheAddress');
  const enterItManuallyText = getTextContent('addressAutocomplete.enterItManually');

  const fieldNameObject = (fieldName + 'Object') as keyof FormDataType;
  const fieldObject = watch(fieldNameObject);

  React.useEffect(() => {
    const hasAddressError = !isOptional ? !isEmpty(addressError) : !isEmpty(addressError) && String(watch(fieldName));
    const validValue = !isEmpty(addressList) && String(watch(fieldName)).length >= MIN_ADDRESS_SEARCH_STRING;

    if (hasAddressError) {
      setError(fieldName, createFormErrorMessage('invalid', addressUnavailableText));
    }

    if (validValue) {
      clearError(fieldName);
      if (!isEmpty(fieldObject)) {
        const { partialAddress } = fieldObject as Partial<AddressDataType>;
        if (partialAddress !== watch(fieldName)) {
          setError(fieldName, createFormErrorMessage('invalid', includeUnitNumberText));
          return;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressError, fieldObject, addressList, clearError, fieldName, searchValue, searching, setError, isOptional]);

  const debouncedSearch = debounce(async (value: string) => {
    const isLengthInvalid = !isOptional
      ? !searching && value.length <= MIN_ADDRESS_SEARCH_STRING
      : !searching && value.length <= MIN_ADDRESS_SEARCH_STRING && value;

    const addressListData = await searchAddress({ searchTerm: value });

    if (isLengthInvalid || (Array.isArray(addressListData) && addressListData.length === 0)) {
      setError(fieldName, createFormErrorMessage('invalid', includeUnitNumberText));
    } else {
      clearError(fieldName);
    }
  }, 500);

  const handleChangeAddress = (e: React.ChangeEvent<object>, value: string) => {
    clearAddressList();

    if (isOptional && !value) {
      setValue(fieldName, '');
      setValue(fieldNameObject, null);
      setValue(addressFieldNames.country, '');
      setValue(addressFieldNames.streetAddress, '');
      setValue(addressFieldNames.suburb, '');
      setValue(addressFieldNames.state, '');
      setValue(addressFieldNames.postCode, '');
    }

    if (e) {
      if (e.type === 'change') {
        setValue(fieldNameIsValid, false);
        debouncedSearch(value);
      }
      setValue(fieldName, value);
      triggerValidation(fieldName);

      if (!isEmpty(fieldObject)) {
        const { partialAddress } = watch(fieldName) as Partial<AddressDataType>;

        if (partialAddress === watch(fieldName)) {
          clearError(fieldName);
          setValue(fieldNameIsValid, true);
        }
      }
    }
  };

  const handleClickManualLink = () => {
    setValue(fieldName, '');
    setValue(fieldNameObject, null, { shouldValidate: true });

    if (fieldObject === null) {
      setValue(fieldNameIsManual, true);
      setValue(addressFieldNames.country, AUSTRALIA);
      setValue(addressFieldNames.streetAddress, '');
      setValue(addressFieldNames.suburb, '');
      setValue(addressFieldNames.state, '');
      setValue(addressFieldNames.postCode, '');
      clearError(fieldNameIsManual);
    } else {
      triggerValidation(fieldNameIsManual);
      triggerValidation(addressFieldNames.country);
      triggerValidation(addressFieldNames.streetAddress);
      triggerValidation(addressFieldNames.suburb);
      triggerValidation(addressFieldNames.state);
      triggerValidation(addressFieldNames.postCode);
    }
  };

  // Manual Address Link
  const displayAddressHelper = () => (
    <>
      <Grid component="span" container item justifyContent="center" xs={12}>
        {get(errors, `${fieldName}.message`)}
      </Grid>
      {cantFindAddressText}&nbsp;
      <Link
        sx={{ cursor: 'pointer' }}
        component="a"
        data-test={`${linkType}-manual-link`}
        onClick={handleClickManualLink}
      >
        {enterItManuallyText}
      </Link>
    </>
  );

  const CustomPaperComponent = (options: React.PropsWithChildren<React.HTMLAttributes<HTMLElement>>) => (
    <Paper elevation={1}>
      {options.children}

      {!isEmpty(addressList) && (
        <ManualTextContainer>
          <AutoCompleteManualText>
            {cantFindAddressText}{' '}
            <Link
              sx={{ cursor: 'pointer' }}
              component="a"
              data-test={`${linkType}-manual-link-autocomplete`}
              onMouseDown={handleClickManualLink}
              underline="always"
            >
              {enterItManuallyText}
            </Link>
          </AutoCompleteManualText>
        </ManualTextContainer>
      )}
    </Paper>
  );

  const CustomPopperComponent = ({ style, ...props }: PopperProps) =>
    !isEmpty(addressList) ? <Popper {...props} style={{ ...style, height: 0 }} placement="bottom" /> : null;

  return (
    <Autocomplete
      // autoComplete={true}
      options={addressList}
      onFocus={() => {
        if (watch(fieldName) && !isEmpty(addressList)) {
          clearError(fieldName);
        }
      }}
      getOptionLabel={(option) => option.partialAddress || ''}
      onInputChange={handleChangeAddress}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      value={(fieldObject || '') as any}
      filterOptions={(options) => options}
      PaperComponent={CustomPaperComponent}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      PopperComponent={CustomPopperComponent as any}
      onChange={async (_, selectedValue) => {
        setValue(fieldNameObject, selectedValue);
        if (!!selectedValue) {
          const data = await getAddressData(selectedValue.moniker);
          if (data) {
            const { buildingName, streetAddress1, city, stateCode, postalCode, country } = data;
            setValue(fieldNameIsValid, true);
            setValue(addressFieldNames.country, country);
            setValue(
              addressFieldNames.streetAddress,
              [buildingName, streetAddress1].filter((str) => str.length).join(', ')
            );
            setValue(addressFieldNames.suburb, city);
            setValue(addressFieldNames.state, stateCode);
            setValue(addressFieldNames.postCode, postalCode);
          }
        }

        return selectedValue;
      }}
      onBlur={() => {
        const isFieldInvalid = !isOptional
          ? !watch(fieldNameIsValid)
          : !watch(fieldNameIsValid) && String(watch(fieldName));

        if (isFieldInvalid && isEmpty(addressError)) {
          setError(fieldName, createFormErrorMessage('invalid', includeUnitNumberText));
        } else if (isFieldInvalid && !isEmpty(addressError)) {
          setError(fieldName, createFormErrorMessage('invalid', addressUnavailableText));
        }

        if (!isEmpty(fieldObject)) {
          const { partialAddress } = fieldObject as Partial<AddressDataType>;

          if (partialAddress === watch(fieldName)) {
            clearError(fieldName);
            setValue(fieldNameIsValid, true);
          }
        }
      }}
      data-track={dataTrack}
      data-track-value={dataTrackValue}
      renderInput={(params) => (
        <TextField
          {...params}
          name={fieldName}
          error={!!get(errors, fieldName)}
          label={label}
          helperText={displayAddressHelper()}
        />
      )}
    />
  );
};

export default AddressAutocomplete;
